import React, { useState, useEffect, useRef } from 'react';
import {
    Container, Typography, Box, Paper, Divider, Select, MenuItem, FormControl,
    InputLabel, TextField, Button, Stack, Grid, Switch, FormControlLabel, FormGroup,
    TextareaAutosize
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAppStore } from '../../../../../store/appStore.tsx';
import { useValue } from '../../../../../context/ContextProvider';
import { updateConfigAlarma } from '../../../../../actions/alarmaActions.js';

// --- Lógica de negocio y hooks (asumimos que están disponibles) ---
/*
const useAppStore = (selector) => ({ currentUser: { token: 'dummy' }, device: { devId: 'dummy' }, startLoading: () => { }, endLoading: () => { } });
const useValue = () => ({ dispatch: () => { } });
const updateConfigAlarma = async (currentUser, devId, command, payload, dispatch) => {
    //console.log("Simulando envío de:", { command, payload });
    await new Promise(resolve => setTimeout(resolve, 500));
    return { resp: 0, insertId: Math.floor(Math.random() * 1000) };
};
*/

// --- Funciones de Lógica de Bits (de tu código original) ---
function buildUint8FromBits(bitConfig = {}) {
    let resultUint8 = 0;
    for (const bitName in bitConfig) {
        if (Object.hasOwnProperty.call(bitConfig, bitName)) {
            const bitInfo = bitConfig[bitName];
            if (bitInfo.value === 1) {
                resultUint8 |= (1 << bitInfo.position);
            }
        }
    }
    return resultUint8;
}

const keyBitDefinitionsTemplate = {
    "bit0": { label: "Configurar", position: 0, value: 0 },
    "bit1": { label: "Armar Alarma", position: 1, value: 0 },
    "bit2": { label: "Desarmar Alarma", position: 2, value: 0 },
    "bit3": { label: "Bit 3", position: 3, value: 0 },
    "bit4": { label: "Bit 4", position: 4, value: 0 },
    "bit5": { label: "Bit 5", position: 5, value: 0 },
    "bit6": { label: "Bit 6", position: 6, value: 0 },
    "bit7": { label: "Bit 7", position: 7, value: 0 },
};

// --- Generador de Estado Inicial (para el padre) ---
export const generarEstadoInicialClaves = () => {
    return Array.from({ length: 16 }, (_, i) => ({
        id: i,
        clave: "------",
        hab: 0,
    }));
};

// --- El Componente del Formulario ---
function ConfiguracionClavesForm({ config, setConfig, state, setState, originalConfigRef}) {
    const { jsonOutput, formDirty, jsonNeedsUpdate, selectedKeyId = 0 } = state;
    const theme = useTheme();
    const { currentUser, device, startLoading, endLoading } = useAppStore();
    const { dispatch } = useValue();

    const [viewState, setViewState] = useState([]);

    // 1. Sincronizar config (del padre) a viewState (local) SOLO al montar o si config cambia externamente
    useEffect(() => {
        if (config && viewState.length === 0) { // Solo inicializar si viewState está vacío
            const newViewState = config.map(key => {
                const keyBitConfig = {};
                for (const bitKey in keyBitDefinitionsTemplate) {
                    const bitValue = (key.hab >> keyBitDefinitionsTemplate[bitKey].position) & 1;
                    keyBitConfig[bitKey] = { ...keyBitDefinitionsTemplate[bitKey], value: bitValue };
                }
                return { id: key.id, clave: key.clave, bitConfig: keyBitConfig };
            });
            setViewState(newViewState);
        }
    }, [config]); // Quitar viewState de aquí para evitar el bucle

    const isMounted = useRef(false);
    useEffect(() => {
        if (isMounted.current && viewState.length > 0) {
            const newConfig = viewState.map(key => ({
                id: key.id,
                clave: key.clave,
                hab: buildUint8FromBits(key.bitConfig),
            }));
            setConfig(newConfig);
        } else {
            isMounted.current = true;
        }
    }, [viewState, setConfig]);

    useEffect(() => {
        const originalConfigString = JSON.stringify(originalConfigRef.current);
        const currentConfigString = JSON.stringify(config);
        const isDirty = originalConfigString !== currentConfigString;

        let needsUpdate = true;
        try {
            const jsonObject = JSON.parse(jsonOutput || '[]');
            needsUpdate = JSON.stringify(jsonObject) !== currentConfigString;
        } catch (e) { needsUpdate = true; }

        if (isDirty !== formDirty || needsUpdate !== jsonNeedsUpdate) {
            setState(prev => ({ ...prev, formDirty: isDirty, jsonNeedsUpdate: needsUpdate }));
        }
    }, [config, jsonOutput, formDirty, jsonNeedsUpdate, originalConfigRef, setState]);

    const handleKeyChange = (event) => {
        setState(prev => ({ ...prev, selectedKeyId: event.target.value }));
    };

    // 3. El efecto de Dirty Check debe ser cuidadoso
    useEffect(() => {
        // Evitamos ejecutar si config no ha cargado
        if (!config || config.length === 0) return;

        const isDirty = JSON.stringify(originalConfigRef.current) !== JSON.stringify(config);

        let needsUpdate = true;
        try {
            const jsonObject = JSON.parse(jsonOutput || '[]');
            needsUpdate = JSON.stringify(jsonObject) !== JSON.stringify(config);
        } catch (e) { needsUpdate = true; }

        if (isDirty !== formDirty || needsUpdate !== jsonNeedsUpdate) {
            setState(prev => ({ ...prev, formDirty: isDirty, jsonNeedsUpdate: needsUpdate }));
        }
    }, [config, jsonOutput]); // Reducir dependencias innecesarias

    // 2. Función unificada para actualizar estados y evitar el useEffect infinito
    const updateAllStates = (newViewState) => {
        setViewState(newViewState);
        
        // Transformamos para el padre inmediatamente
        const newConfig = newViewState.map(key => ({
            id: key.id,
            clave: key.clave,
            hab: buildUint8FromBits(key.bitConfig),
        }));
        
        // Solo llamar al padre si realmente es necesario
        setConfig(newConfig);
    };

    const handleClaveChange = (event) => {
        const value = event.target.value;
        if (/^[0-9]*$/.test(value) && value.length <= 6) {
            const newViewState = viewState.map((key, index) => {
                if (index === selectedKeyId) return { ...key, clave: value };
                return key;
            });
            updateAllStates(newViewState); // <--- Usamos la función unificada
        }
    };

    const handleBitChange = (bitName, checked) => {
        const newViewState = viewState.map((key, index) => {
            if (index === selectedKeyId) {
                const updatedBitConfig = { ...key.bitConfig };
                updatedBitConfig[bitName] = { ...updatedBitConfig[bitName], value: checked ? 1 : 0 };
                return { ...key, bitConfig: updatedBitConfig };
            }
            return key;
        });
        updateAllStates(newViewState); // <--- Usamos la función unificada
    };

    const handleGenerateJson = () => {
        setState(prev => ({ ...prev, jsonOutput: JSON.stringify(config, null, 2), jsonNeedsUpdate: false }));
    };

    /*
    const sendConfig = async () => {
        startLoading();
        const result = await updateConfigAlarma(currentUser, device.devId, "CMD_SET_CONFIG_CLAVES", config, dispatch);
        if (result?.resp === 0) {
            originalConfigRef.current = JSON.parse(JSON.stringify(config));
            setState(prev => ({ ...prev, formDirty: false }));
            alert(`Configuración de Claves enviada con éxito (ID: ${result.insertId})`);
        } else {
            alert("Error al enviar la configuración de Claves.");
        }
        endLoading();
    };
    */

    const sendConfig = async () => {
        //console.log("--- Enviando Configuración JSON ---");
        startLoading();
        setState(prev => ({ ...prev, configId: "---", estado: "Esperando respuesta del servidor/dispositivo", color: "black"}));
        const updateResult = await updateConfigAlarma(currentUser, device.devId, "CMD_SET_CONFIG_CLAVES", config, dispatch);
        //console.log("updateResult: ", updateResult);

        if (updateResult?.deviceResponse === 0) {
            // Si el envío es exitoso, actualizamos la configuración original
            originalConfigRef.current = JSON.parse(JSON.stringify(config));
            setState(prev => ({ ...prev, formDirty: false, configId: updateResult.insertId, estado: "Última configuración actualizada con éxito.", color: "green"}));
            alert(`Configuración enviada con éxito (ID: ${updateResult.insertId})`);
        } else {
            setState(prev => ({ ...prev, configId: 0, estado: "Error. Última configuración NO actualizada.", color: "red"}));
            alert("Error al enviar la configuración.");
        }
        endLoading();
    };

    const handleCancel = () => {
        if (window.confirm("¿Seguro que quieres descartar los cambios?")) {
            setConfig(originalConfigRef.current);
        }
    };

    const currentKeyData = viewState[selectedKeyId] || { clave: '', bitConfig: {} };
    const calculatedUint8 = buildUint8FromBits(currentKeyData.bitConfig);

    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h5" component="h1" gutterBottom>Configuración de Claves</Typography>
                <Divider sx={{ mb: 2 }} />
                <Box component="form" onSubmit={(e) => { e.preventDefault(); sendConfig(); }}>
                    <Grid container spacing={4}>
                        {/* Columna izquierda: Formulario */}
                        <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Seleccionar Clave</InputLabel>
                                    <Select value={selectedKeyId} label="Seleccionar Clave" onChange={handleKeyChange}>
                                        {viewState.map(key => <MenuItem key={key.id} value={key.id}>Clave {key.id+1}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <Divider sx={{ mb: 2 }}><Typography variant="subtitle1">Datos para Clave {selectedKeyId}</Typography></Divider>
                                <TextField fullWidth label="Contraseña (4-6 dígitos numéricos)" type="text" value={currentKeyData.clave} onChange={handleClaveChange}
                                    margin="normal"
                                    inputProps={{ minLength: 4, maxLength: 6, pattern: "[0-9]*", inputMode: "numeric" }}
                                    helperText="Solo números (0-9)"
                                />
                                <FormGroup sx={{ mt: 2 }}>
                                    {Object.keys(currentKeyData.bitConfig).map(bitName => (
                                        <FormControlLabel key={bitName} control={
                                            <Switch checked={!!currentKeyData.bitConfig[bitName].value} onChange={(e) => handleBitChange(bitName, e.target.checked)} />
                                        } label={currentKeyData.bitConfig[bitName].label} />
                                    ))}
                                </FormGroup>
                            </Paper>
                        </Grid>

                        {/* Columna derecha: JSON y Resumen */}
                        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* --- PANEL DE JSON (CÓDIGO PROPORCIONADO POR EL USUARIO) --- */}
                            <Paper variant="outlined" sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative', pt: 4 }}>
                                <Typography
                                    variant="caption"
                                    component="div"
                                    sx={{
                                        position: 'absolute', top: 0, left: 12,
                                        transform: 'translateY(-50%)', bgcolor: 'background.paper',
                                        px: 1, color: 'primary.main', fontWeight: 'bold',
                                        fontSize: '0.8rem', textTransform: 'uppercase',
                                    }}
                                >
                                    JSON de Configuración
                                </Typography>
                                {jsonOutput ? (
                                    <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
                                        <TextareaAutosize
                                            value={jsonOutput}
                                            minRows={10}
                                            maxRows={20}
                                            style={{
                                                width: '100%',
                                                border: `1px solid ${jsonNeedsUpdate ? theme.palette.warning.main : theme.palette.divider}`,
                                                borderRadius: '4px',
                                                padding: '8px',
                                                fontFamily: 'monospace',
                                                resize: 'vertical',
                                                backgroundColor: jsonNeedsUpdate ? '#ffe0b2' : theme.palette.background.paper,
                                                color: theme.palette.text.primary,
                                            }}
                                            readOnly
                                        />
                                    </Box>
                                ) : (
                                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Typography color="text.secondary">
                                            Genera el JSON para previsualizarlo aquí.
                                        </Typography>
                                    </Box>
                                )}
                                <Button
                                    variant="contained"
                                    fullWidth
                                    color={jsonNeedsUpdate ? "warning" : "primary"}
                                    onClick={handleGenerateJson}
                                    sx={{ mt: 'auto' }}
                                >
                                    {jsonNeedsUpdate ? "Actualizar JSON" : "Generar JSON"}
                                </Button>
                            </Paper>
                            <Paper variant="outlined" sx={{ p: 2, position: 'relative', pt: 4 }}>
                                <Typography variant="caption" component="div" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold' }}>RESUMEN DE CONFIGURACIÓN</Typography>
                                <Typography variant="h6" gutterBottom>Clave {selectedKeyId}</Typography>
                                <Typography variant="body2">**Contraseña:** {currentKeyData.clave || 'N/A'}</Typography>
                                <Typography variant="body2">**Valor Habilitación (uint8):** {calculatedUint8}</Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>Binario: {calculatedUint8.toString(2).padStart(8, '0')}</Typography>
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" color={formDirty ? 'error' : 'inherit'}>**Formulario Modificado:** {formDirty ? 'Sí' : 'No'}</Typography>
                                    <Typography variant="body2" color={jsonNeedsUpdate ? 'warning.main' : 'inherit'}>**JSON Actualizado:** {jsonNeedsUpdate ? 'No' : 'Sí'}</Typography>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Fila inferior: Resultados del envio de la configuracion al dispositivo */}
                        <Grid item xs={12} md={12}>
                            <Paper variant="outlined" sx={{ p: 2, height: '100%', position: 'relative', pt: 4 }}>
                                <Typography variant="caption" component="div" sx={{position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold', fontSize: '0.8rem', textTransform: 'uppercase'}}>
                                    Resultado envío al Dispositivo
                                </Typography>
                                <Box>
                                    <Typography variant="body2" component="p" sx={{ ml: 2, fontFamily: 'monospace', mb: 0.5 }}>
                                        <strong>ConfigId: </strong>{state.configId}
                                    </Typography>
                                    <Typography variant="body2" component="p" sx={{ ml: 2, fontFamily: 'monospace', mb: 0.5, color: state.color }}>
                                        <strong>Estado: </strong>{state.estado}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Botones de Acción */}
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                                <Button variant="outlined" color="secondary" onClick={handleCancel} disabled={!formDirty}>Cancelar</Button>
                                <Button type="submit" variant="contained" disabled={jsonNeedsUpdate}>Enviar</Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        </Container>
    );
}

export default ConfiguracionClavesForm;